import { Attr, BelongsTo, HasMany, HasOne, Model } from "spraypaint";

import { ApplicationRecord } from "shared/services/spraypaint";
import type { Nullable } from "shared/types/utility";

import type ContactList from "./ContactList";
import type ContactListsContact from "./ContactListsContact";
import type InfluencerAddress from "./InfluencerAddress";
import type InfluencerBusinessCard from "./InfluencerBusinessCard";
import type InfluencerOutlet from "./InfluencersOutlet";
import type InfluencersTopic from "./InfluencersTopic";
import type Outlet from "./Outlet";
import type OutreachTopic from "./OutreachTopic";
import type TwitterUser from "./TwitterUser";
import type { Email, Homepage, InternalId, Phone, Social } from "./types";

@Model()
class Influencer extends ApplicationRecord {
  static jsonapiType = "influencers";

  @Attr() archivedAt: string;

  @Attr() avatarUrl?: string;

  @Attr() averageFacebookEngagement: number;

  @Attr() averagePinterestShares: number;

  @Attr() averageRedditEngagements: number;

  @Attr() averageTotalEngagements: number;

  @Attr() averageTwitterShares: number;

  @Attr() biography: string;

  @Attr() businessCardTitles: Nullable<string[]>;

  @HasMany() contactLists: ContactList[];

  @HasMany() contactListsContacts: ContactListsContact[];

  @Attr() displayBio: string;

  @Attr({ persist: false }) documentId: string;

  @Attr() dominantFacebookReaction: string;

  @Attr() emails: Email[];

  @Attr() faxes: Phone[];

  @Attr() firstName: string;

  @Attr() gender: string;

  @Attr() highResAvatarUrl: string;

  @Attr() homepages: Homepage[];

  @HasMany({ name: "influencerAddresses" })
  influencerAddresses: InfluencerAddress[];

  @HasMany() InfluencerBusinessCards: InfluencerBusinessCard[];

  @HasMany() influencerOutreachTopics: OutreachTopic[];

  @HasMany({ name: "influencerOutlets" })
  influencersOutlets: InfluencerOutlet[];

  @HasMany({ name: "influencersTopics" })
  influencersTopics: InfluencersTopic[];

  @Attr() internalIds: InternalId[];

  @Attr() invalidEmail: boolean;

  @Attr() languages: string[];

  @Attr() lastName: string;

  @Attr() location: string;

  @Attr() middleName: string;

  @Attr() name: string;

  @Attr() optedType: string;

  @HasOne() outlet: Outlet;

  @Attr() outletNames: string[];

  @HasMany({ name: "outlets" }) outlets: Outlet[];

  @Attr() phones: Phone[];

  @Attr() primaryOutletId: bigint;

  @Attr() primaryOutletName: string;

  @Attr() profile: string;

  @Attr() salutation: string;

  @Attr() socials: Social[];

  @Attr() status: string;

  @Attr() suffix: string;

  @Attr() topFacebookReaction: string;

  @Attr() totalAngryReactions: number;

  @Attr() totalHahaReactions: number;

  @Attr() totalLoveReactions: number;

  @Attr() totalSadReactions: number;

  @Attr() totalWowReactions: number;

  @BelongsTo() twitterUser: TwitterUser;

  @Attr() twitterUsername: string;

  @Attr() verifiedBy: string;

  @Attr() verifiedDate: string;

  get email(): string | undefined {
    return this.emails?.find((email) => email.defaultFlag)?.email;
  }
}

export default Influencer;
